import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { TOPBAR_H, SIDEBAR_W } from '../config/layout';

/**
 * PC 화면 껍데기 — 상단 바 + 사이드바 + 본문 (2026-09-21 개편)
 *
 * VIMS 와 같은 형태로 맞췄다. 로고·버전·시계·날씨·사용자·비밀번호 변경은 상단 바에 있고,
 * 사이드바에는 메뉴만 남는다. 상단 바의 ☰ 로 사이드바를 접었다 펼 수 있다.
 */
const MainLayout = ({ user, menus, onLogout, onMenuClick, children }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);  // 모바일에서 슬라이딩 메뉴 열림 여부
  const [isCollapsed, setIsCollapsed] = useState(false);          // PC 에서 사이드바 접힘 여부

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setIsSidebarOpen(!mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ☰ 는 화면 크기에 따라 다르게 동작한다 — 좁으면 메뉴를 밀어서 열고, 넓으면 접는다.
  const handleToggle = () => {
    if (isMobile) setIsSidebarOpen(v => !v);
    else setIsCollapsed(v => !v);
  };

  const sidebarHidden = isMobile ? !isSidebarOpen : isCollapsed;

  const styles = {
    container: { minHeight: '100vh', width: '100vw', background: '#f1f5f9', boxSizing: 'border-box' },

    sidebarWrapper: {
      width: SIDEBAR_W + 'px',
      position: 'fixed',
      top: TOPBAR_H + 'px',
      left: sidebarHidden ? `-${SIDEBAR_W}px` : '0',
      height: `calc(100vh - ${TOPBAR_H}px)`,
      background: '#243447',
      zIndex: 2000,
      transition: 'left 0.25s ease'
    },

    mainArea: {
      marginTop: TOPBAR_H + 'px',
      marginLeft: sidebarHidden ? '0' : SIDEBAR_W + 'px',
      width: sidebarHidden ? '100vw' : `calc(100vw - ${SIDEBAR_W}px)`,
      minHeight: `calc(100vh - ${TOPBAR_H}px)`,
      padding: isMobile ? '20px' : '0',
      boxSizing: 'border-box',
      transition: 'margin-left 0.25s ease, width 0.25s ease'
    },

    // 모바일에서 메뉴를 열었을 때 배경 어둡게
    overlay: {
      display: (isMobile && isSidebarOpen) ? 'block' : 'none',
      position: 'fixed', top: TOPBAR_H + 'px', left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 1500
    }
  };

  return (
    <div style={styles.container}>
      <TopBar
        user={user}
        onLogout={onLogout}
        onToggleSidebar={handleToggle}
        compact={isMobile}
      />

      <div style={styles.overlay} onClick={() => setIsSidebarOpen(false)} />

      <div style={styles.sidebarWrapper}>
        <Sidebar
          user={user}
          menus={menus}
          onMenuClick={(menu) => {
            onMenuClick(menu);
            if (isMobile) setIsSidebarOpen(false);   // 모바일은 고르면 메뉴를 닫는다
          }}
        />
      </div>

      <main style={styles.mainArea}>
        {/* 외부 공개용 읽기 전용 계정에게는 조회만 가능하다는 것을 항상 알려준다. */}
        {user?.role === 'VIEWER' && (
          <div style={{
            background: '#fffbeb', borderBottom: '1px solid #fde68a', color: '#92400e',
            padding: '12px 20px', fontSize: '13px', fontWeight: '700',
            display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            👁️ 읽기 전용(모니터링) 계정입니다 — 조회만 가능하며 입력·수정·삭제는 할 수 없습니다.
          </div>
        )}
        {children}
      </main>
    </div>
  );
};

export default MainLayout;
